import { useContext, useLayoutEffect, useRef, useState, type ComponentProps, type ReactNode } from "react";
import { Command as CommandPrimitive } from "cmdk";
import { CheckIcon } from "lucide-react";

import { cn } from "../../lib/utils";
import { SelectCollectingContext, SelectContentContext, SelectPinnedContext, useSelectContext } from "./SelectContext";

export type SelectItemProps = Omit<ComponentProps<typeof CommandPrimitive.Item>, "value" | "onSelect" | "keywords"> & {
  value: string;
  /** The text searched and ranked against. Defaults to string children, else the item's rendered text. */
  textValue?: string;
  /** Extra terms the search should match (e.g. an abbreviation). */
  keywords?: string[];
  /** How the item appears once selected — on the trigger and in multi-select badges. Defaults to its children; set it when the option row is richer than a label (avatars, secondary lines). */
  label?: ReactNode;
};

export const SelectItem = ({
  value,
  textValue,
  keywords = [],
  label,
  disabled,
  className,
  children,
  ...props
}: SelectItemProps) => {
  const collecting = useContext(SelectCollectingContext);
  const pinned = useContext(SelectPinnedContext);
  const hideSelectedItems = useContext(SelectContentContext)?.hideSelectedItems ?? false;
  const { isSelected, selectItem, registerItem, rememberItem, maxCountReached } = useSelectContext();
  const itemRef = useRef<HTMLDivElement>(null);
  const [renderedText, setRenderedText] = useState("");
  const plainText = (node: ReactNode) =>
    typeof node === "string" || typeof node === "number" ? String(node) : undefined;
  const resolvedTextValue = textValue ?? plainText(children) ?? plainText(label) ?? renderedText;

  // The trigger renders labels from the root's registry, which lives outside this item and can't be
  // written during render. Layout timing lands a changed label before paint, not a frame late.
  useLayoutEffect(() => {
    if (!pinned) rememberItem(value, { node: label ?? children, textValue: resolvedTextValue, disabled });
  });

  // Tied to this item's mount lifecycle so the root's count reflects what is actually rendered; layout
  // timing settles the count (and with it, search visibility) before the dropdown paints and auto-focuses.
  useLayoutEffect(() => {
    if (!pinned) return registerItem(value);
  }, [pinned, registerItem, value]);

  // JSX children without a `textValue` are searched by their rendered text, read back from the DOM.
  useLayoutEffect(() => {
    const text = itemRef.current?.textContent?.trim() ?? "";
    if (text !== renderedText) setRenderedText(text);
  });

  const selected = isSelected(value);
  if (collecting || (hideSelectedItems && selected)) return null;

  return (
    <CommandPrimitive.Item
      ref={itemRef}
      data-slot="select-item"
      data-state={selected ? "checked" : "unchecked"}
      value={value}
      keywords={[resolvedTextValue, ...keywords]}
      disabled={disabled || (maxCountReached && !selected)}
      onSelect={() => selectItem(value)}
      className={cn(
        "relative flex w-full cursor-pointer items-center gap-2 rounded-sm py-1.5 pr-8 pl-2 text-sm outline-none select-none group-data-[indicator-side=left]/select-content:pr-2 group-data-[indicator-side=left]/select-content:pl-8 data-[disabled=true]:pointer-events-none data-[disabled=true]:opacity-50 data-[selected=true]:bg-accent data-[selected=true]:text-accent-foreground [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 [&_svg:not([class*='text-'])]:text-muted-foreground",
        className
      )}
      {...props}
    >
      <span className="absolute right-2 flex size-3.5 items-center justify-center group-data-[indicator-side=left]/select-content:right-auto group-data-[indicator-side=left]/select-content:left-2">
        {selected && <CheckIcon className="size-4" />}
      </span>
      {children}
    </CommandPrimitive.Item>
  );
};
